import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"

import Theme from "@airasia/phoenix-components/components/Theme"
import UniversalHeader from "@airasia/phoenix-widgets/widgets/UniversalHeader"
import AVAChatbot from "@airasia/phoenix-widgets/widgets/AVAChatbot"
import Title from "@airasia/phoenix-components/components/Title"
import EmojiScoring from "@airasia/phoenix-components/components/EmojiScoring"
import UniversalFooter from "@airasia/phoenix-widgets/widgets/UniversalFooter"

import SEO from "../components/seo"
import Breadcrumb from "../components/breadcrumb"
import DesktopBanner from "../components/desktopBanner"

import HERO_BANNER_DEFAULT_DATA from "../data/phoenix/heroBanner"
import EMOJI_SCORING_DATA from "../data/phoenix/emojiScoring"
import {
  AppCtaDesktopWrapper,
  AppCtaMobileWrapper,
} from "./AppCTABanner/staticDataWrapper"

import { GTM_PROJECT_CATEGORY } from "../utils/constants"
import { getGtmUserType, fireGtmEvent } from "../utils/functions"
import { MobileViewWrapper, DesktopViewWrapper } from "../utils/styles"
import WebVitalRUM from "@airasia/phoenix-components/components/WebVitalRUM"
import { withPrefix } from "gatsby"

const Wrapper = styled.div`
  max-width: 625px;
  h2 {
    font-size: 1.25em;
  }

  h1 {
    font-size: 1.5em;
  }

  @media only screen and (min-width: 720px) {
    width: 90%;
    max-width: 976px;
    margin: 0px auto 48px;

    h1 {
      font-size: 2.5em;
    }

    h2 {
      font-size: 1.5em;
    }
  }

  @media only screen and (min-width: 1025px) {
    width: 90%;
    max-width: 1224px;
    margin: 0px auto 48px;
  }
`

const EmojiFeedbackWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 10px;
  align-items: center;

  @media only screen and (max-width: 768px) {
    margin: 0px;
    margin-bottom: 10px;
  }

  #emojiFeedbackTitle {
    margin: 15px;
  }
`

// The two maps need to be done because uhf requires locale in format of "en-gb"
// "[language]-[country] all small caps"
const languageToUhfMap = {
  en: "en-gb",
  th: "th-th",
  id: "id-id",
  ms: "ms-my",
}

const uhfLocaleToUrlMap = {
  "en-gb": "/",
  "th-th": "/th/th/",
  "id-id": "/id/id/",
  "ms-my": "/ms/",
}

const Layout = ({ pageData, children }) => {
  const { location, type, faq, metaTags, pageHeading, layout } = pageData

  const breadcrumb = layout.breadcrumb

  const [isMobile, setIsMobile] = useState(true)

  const pageConfiguration = layout.configuration
  const baseSlug = layout.baseSlug
  const pageLanguage = pageConfiguration.language
  const pageComponentTranslation = layout.pageComponentTranslation

  const header_data = pageComponentTranslation.header
  const footer_data = pageComponentTranslation.footer
  const hsw_data = pageComponentTranslation.hsw
  const emojiFeedback_data = pageComponentTranslation.emojiFeedback
  const mobileBannerCta = pageComponentTranslation.mobileBannerCta

  const uhfLocale = languageToUhfMap[pageLanguage]
  header_data.localeList = [{ data: layout.uhfLocales }]

  pageData.heroBanner = pageData.heroBanner || HERO_BANNER_DEFAULT_DATA

  const onWindowResize = () => {
    if (typeof window !== undefined) {
      if (window.innerWidth <= 768) {
        setIsMobile(true)
      } else {
        setIsMobile(false)
      }
    }
  }

  useEffect(() => {
    onWindowResize()

    window.addEventListener("resize", onWindowResize)
    return () => window.removeEventListener("resize", onWindowResize)
  }, [])

  // for locale change from uhf
  const updateLocale = obj => {
    window.location.href = withPrefix(baseSlug + uhfLocaleToUrlMap[obj.detail])
  }

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("uhfLocaleChange", updateLocale)
    }
  }, [])

  const avaGtmFire = () => {
    const obj = {
      event: "trackEventNoEcommerce",
      "eventDetails.category": `AVA Chat Bot`,
      "eventDetails.action": `click`,
      "eventDetails.label": `${GTM_PROJECT_CATEGORY} | ${getGtmUserType()}`,
    }

    fireGtmEvent(obj)
  }

  const onEmojiClicked = val => {
    const gtmEvent = {
      event: "trackEventNoEcommerce",
      "eventDetails.category": `emoji scoring`,
      "eventDetails.action": `click`,
      "eventDetails.label": `${GTM_PROJECT_CATEGORY} | ${val} | ${getGtmUserType()}`,
    }

    fireGtmEvent(gtmEvent)
  }

  return (
    <Theme theme="AIRASIA_CLASSIC">
      <SEO
        location={location}
        type={type}
        breadcrumb={breadcrumb}
        faq={faq}
        metaTags={metaTags}
        pageLanguage={pageLanguage}
      />
      <WebVitalRUM fireGtm={fireGtmEvent} />
      <DesktopViewWrapper>
        <UniversalHeader
          data={header_data}
          locale={uhfLocale}
          currency="MYR"
          environment="production"
          viewport="desktop"
          loginConfig={{
            HideLoginIconForMobile: false,
            enableBigPoints: false,
            reloadOnLoginStatusChange: true,
            shouldSetCookie: false,
          }}
        />
      </DesktopViewWrapper>

      <MobileViewWrapper>
        <UniversalHeader
          data={header_data}
          locale={uhfLocale}
          currency="MYR"
          environment="production"
          viewport="mobile"
          loginConfig={{
            HideLoginIconForMobile: false,
            enableBigPoints: false,
            reloadOnLoginStatusChange: true,
            shouldSetCookie: false,
          }}
        />
      </MobileViewWrapper>

      <DesktopBanner
        location={location}
        type={type}
        pageHeading={pageHeading}
        hsw_data={hsw_data}
        mobileBannerCta={mobileBannerCta}
      />
      <Wrapper>
        {/* {breadcrumb && (
          <div style={{ marginTop: "2%" }}>
            <Breadcrumb crumbs={breadcrumb} />
          </div>
        )} */}

        {children}
      </Wrapper>

      <EmojiFeedbackWrapper>
        <Title type="h3" customProperties={{ id: "emojiFeedbackTitle" }}>
          {emojiFeedback_data.title}
        </Title>
        <EmojiScoring
          data={EMOJI_SCORING_DATA}
          fullWidth={false}
          maxText={emojiFeedback_data.minText}
          minText={emojiFeedback_data.maxText}
          onEmojiClicked={onEmojiClicked}
          textColor="greyDark"
          textSize="small"
        />
      </EmojiFeedbackWrapper>

      {!isMobile ? (
        <DesktopViewWrapper>
          <AVAChatbot
            adaEmbedConfig={{
              metaFields: {
                platform: "MOBILE",
                source: "SEO All Hotels",
              },
              parentElement: "Avaembed",
            }}
            language={languageToUhfMap[pageLanguage]}
            isMobile={false}
            gtmEventData="null" // passing empty string without which fireGtm will not be triggered
            fireGtm={() => {
              avaGtmFire()
            }}
            accessible
            adaWindowPersistence
            avaPositionConfig={{
              bottom: "40px",
              left: "auto",
              right: "50px",
              top: "auto",
            }}
            dataConfig={{
              productionID: "airasia",
              stagingID: "airasia-sandbox",
              tooltipLabel: "Chat with AVA",
            }}
            enableLogin={false}
            env="prod"
            imageConfig={{
              height: "46px",
              imageURL: "https://a.staticaa.com/images/misc/ava-blurb.png",
              radius: "50%",
              width: "46px",
            }}
            isLogin={false}
            loadAVAScript
            openAvaOnLogi
          />
          {AppCtaDesktopWrapper[pageLanguage]}
          <UniversalFooter viewport="desktop" data={footer_data} />
        </DesktopViewWrapper>
      ) : (
        <MobileViewWrapper>
          <AVAChatbot
            adaEmbedConfig={{
              metaFields: {
                platform: "MOBILE",
                source: "SEO All Hotels",
              },
              parentElement: "Avaembed",
            }}
            language={languageToUhfMap[pageLanguage]}
            isMobile={true}
            gtmEventData="null" // passing empty string without which fireGtm will not be triggered
            fireGtm={() => {
              avaGtmFire()
            }}
            accessible
            adaWindowPersistence
            avaPositionConfig={{
              bottom: "40px",
              left: "auto",
              right: "50px",
              top: "auto",
            }}
            dataConfig={{
              productionID: "airasia",
              stagingID: "airasia-sandbox",
              tooltipLabel: "Chat with AVA",
            }}
            enableLogin={false}
            env="prod"
            imageConfig={{
              height: "46px",
              imageURL: "https://a.staticaa.com/images/misc/ava-blurb.png",
              radius: "50%",
              width: "46px",
            }}
            isLogin={false}
            loadAVAScript
            openAvaOnLogin={false}
            sourceV2
          />
          {AppCtaMobileWrapper[pageLanguage]}
          <UniversalFooter viewport="mobile" data={footer_data} />
        </MobileViewWrapper>
      )}
    </Theme>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
}

export default Layout
